import { getCalendarEvents, updateCalendarEvent } from "@rpforms/shared/build/actions";
import { fetchEmployees } from "@rpforms/shared/build/actions/employees";

import { getDevices } from "@rpforms/shared/build/actions/devices";
import { getCalendarEventParticipants } from "@rpforms/shared/build/actions/calendar_event_participants";
import moment from "moment";
import "moment/locale/de";
import React, { useEffect, useState } from "react";
import "react-big-calendar/lib/css/react-big-calendar.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { Col, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import { RootState } from "../../../reducers";
import {
    BaseCalendar,
    mapVacationsFromEmployeeForCalendar,
    onSelectRegularCalendarEvent,
    onSelectVacationEvent,
} from "../../calendar/BaseCalendar";
import CalendarEventTypeSelector from "../../calendar/CalendarEventTypeSelector";

const DnDCalendar = withDragAndDrop(BaseCalendar);

const CustomEvent = ({ event }) => {
    const tooltip = (
        <Tooltip id={`tooltip-${event.id}`}>
            <div className="text-left">
                <strong>{event.title}</strong>
                <div>
                    {moment(event.start).format("HH:mm")} - {moment(event.end).format("HH:mm")}
                </div>
                {event.location && <div>Ort: {event.location}</div>}
                {event.participantNames && <div>Teilnehmer: {event.participantNames}</div>}
                {event.notes && <div className="mt-1"><em>{event.notes}</em></div>}
            </div>
        </Tooltip>
    );

    return (
        <OverlayTrigger placement="auto" overlay={tooltip}>
            <div>
                <div>{event.title}</div>
                {event.participantNames && (
                    <div style={{ fontSize: "0.8em" }}>Teilnehmer: {event.participantNames}</div>
                )}
            </div>
        </OverlayTrigger>
    );
};

export const EventCalendar = () => {
    const calendarEventState = useSelector<any, any>((state) => state.calendarEvents);
    const events = useSelector<any, any>((state) => state.calendarEvents.calendarEvents);
    const deviceState = useSelector<any, any>((state) => state.device);
    const types = useSelector<RootState, any>((store) => store.calendarEventTypes.types);
    const employees = useSelector<RootState, any>((state) => state.employees.employees);
    const allParticipants = useSelector<RootState, any>(
        (store) => store.calendarEventParticipants.participants
    );

    const dispatch = useDispatch();
    const [showSubmittedEvents, setShowSubmittedEvents] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(0);
    const [selectedType, setSelectedType] = useState(null);
    const [showVacations, setShowVacations] = useState(true);
    const [range, setRange] = useState({
        start: moment(+new Date()).startOf("week"),
        end: moment(+new Date()).endOf("week"),
    });

    const handleFilters = (events: any[]): any[] => {
        const ausfuehrungsId = types.find((t) => t.name === "Ausführung")?.id;

        let filteredEvents = events.filter((event) => {
            if (!selectedType || selectedType.id === -1) return true;

            if (ausfuehrungsId && selectedType.id === ausfuehrungsId) {
                return (
                    event.calendar_event_type_id === ausfuehrungsId || !event.calendar_event_type_id
                );
            }

            return event.calendar_event_type_id === selectedType.id;
        });

        if (!showSubmittedEvents) {
            filteredEvents = filteredEvents.filter((e) => !e.was_submitted);
        }

        if (selectedDevice !== 0) {
            filteredEvents = filteredEvents.filter((event) => event.device_id === selectedDevice);
        }

        if (showVacations) {
            filteredEvents = filteredEvents.concat(mapVacationsFromEmployeeForCalendar(employees));
        }

        return filteredEvents;
    };

    useEffect(() => {
        dispatch(getDevices({ forSelect: true }));
        dispatch(fetchEmployees());
        dispatch(getCalendarEventParticipants());
    }, []);

    useEffect(() => {
        dispatch(getCalendarEvents(range));
    }, [range]);

    const devices = deviceState.devices;

    const mappedDevicesForSelect = [
        { value: 0, label: "Alle" },
        { value: null, label: "Ohne Gerät" },
    ].concat(
        devices.map((device) => {
            return { value: device.id, label: device.name };
        })
    );

    const eventStyleGetter = (event, start, end, isSelected) => {
        const ausführungsId = types.find((t) => t.name === "Ausführung")?.id;
        const type = types.find((t) => t.id === event.calendar_event_type_id);
        const isAusführung =
            event.calendar_event_type_id === ausführungsId || !event.calendar_event_type_id;
        const device = devices.find((d) => event.device_id === d.id);

        const getBackgroundColor = () => {
            if (event.color) return event.color;
            if (!isAusführung) return type?.color;
            return device?.calendar_color || "#3174ad";
        };

        return {
            backgroundColor: getBackgroundColor(),
            fontWeight: event.was_submitted ? "normal" : "bold",
            fontStyle: event.was_submitted ? "italic" : "normal",
        };
    };

    const onSelectEvent = (event) => {
        if (event.employee_id) return onSelectVacationEvent(event, dispatch);
        return onSelectRegularCalendarEvent(event);
    };

    const setNewRange = (l) => {
        setRange({
            start: moment(l[0]).startOf("day"),
            end: moment(l[l.length - 1]).endOf("day"),
        });
    };

    const getAschurWorkDays = (currentDate) => {
        // Cycle: 5 days - 3 days - 5 days - 2 days
        // Reference: Week of Dec 8, 2025 is a 2-day week.
        const referenceDate = moment("2025-12-08", "YYYY-MM-DD").startOf("isoWeek");
        const weeksDiff = currentDate.clone().startOf("isoWeek").diff(referenceDate, "weeks");
        
        const cycle = [2, 5, 3, 5];
        
        const index = ((weeksDiff % 4) + 4) % 4;
        return cycle[index];
    };

    const aschurDays = getAschurWorkDays(range.start);

    const mapEventForDisplay = (event: any) => {
        const eventStart = event.start || event.scheduled_from;
        const eventEnd = event.end || event.scheduled_to;

        let offerTitle = null;
        if (event.device_tasks && event.device_tasks.length > 0) {
            const offerTask = event.device_tasks.find(
                (dt: any) => dt.form_entry?.name
            );
            if (offerTask) {
                offerTitle = offerTask.form_entry.name;
            }
        }

        let eventTitle = event.calendar_title || "Urlaub: " + event.title;
        if (offerTitle) {
            eventTitle = `${eventTitle} (${offerTitle})`;
        }

        let participantNames = null;

        const ausfuehrungsId = types.find((t) => t.name === "Ausführung")?.id;
        const isAusführung =
            event.calendar_event_type_id === ausfuehrungsId || !event.calendar_event_type_id;

        if (isAusführung && allParticipants && allParticipants.length > 0) {
            const eventParticipants = allParticipants
                .filter((p) => p.calendar_event_id === event.id)
                .map((p) => p.employee_id);

            participantNames = employees
                .filter((e) => eventParticipants.includes(e.id))
                .map((e) => e.name)
                .join(", ");
        }

        return {
            ...event,
            start: moment(eventStart).toDate(),
            end: moment(eventEnd).toDate(),
            title: eventTitle,
            participantNames: participantNames,
            allDay: event.employee_id,
        };
    };

    const handleEventDrop = ({ event, start, end }) => {
        const updatedEvent = {
            id: event.id,
            name: event.name,
            device_id: event.device_id,
            scheduled_from: moment(start).format("YYYY-MM-DDTHH:mm:ssZ"),
            scheduled_to: moment(end).format("YYYY-MM-DDTHH:mm:ssZ"),
            location: event.location,
            notes: event.notes,
            calendar_event_type_id: event.calendar_event_type_id,
            entity_id: event.entity_id,
            contactperson: event.contactperson,
            phone: event.phone,
            protocol_type: event.protocol_type,
            object_label: event.object_label,
            was_submitted: event.was_submitted,
        };
        
        dispatch(updateCalendarEvent(updatedEvent, () => {
            toast.success("Termin verschoben", { autoClose: 2000 });
            dispatch(getCalendarEvents(range));
        }));
    };

    const handleEventResize = ({ event, start, end }) => {
        const updatedEvent = {
            id: event.id,
            name: event.name,
            device_id: event.device_id,
            scheduled_from: moment(start).format("YYYY-MM-DDTHH:mm:ssZ"),
            scheduled_to: moment(end).format("YYYY-MM-DDTHH:mm:ssZ"),
            location: event.location,
            notes: event.notes,
            calendar_event_type_id: event.calendar_event_type_id,
            entity_id: event.entity_id,
            contactperson: event.contactperson,
            phone: event.phone,
            protocol_type: event.protocol_type,
            object_label: event.object_label,
            was_submitted: event.was_submitted,
        };
        
        dispatch(updateCalendarEvent(updatedEvent, () => {
            toast.success("Terminzeit geändert", { autoClose: 2000 });
            dispatch(getCalendarEvents(range));
        }));
    };

    const draggableAccessor = (event) => {
        return true;
    };

    const resizableAccessor = (event) => {
        return true;
    };

    const components = React.useMemo(() => ({
        event: CustomEvent,
    }), []);

    return (
        <>
            <Row>
                <Col>
                    <label>Wählen Sie einen Eventtypen aus</label>
                    <CalendarEventTypeSelector
                        types={[
                            {
                                id: -1,
                                name: "Alle",
                                color: "rgba(0,0,0,0)",
                            },
                            ...types.filter(
                                (type) => !["Seminar", "Besichtigung"].includes(type?.name)
                            ),
                        ]}
                        eventType={selectedType}
                        setFirstAsDefault
                        setEventType={setSelectedType}
                    />
                </Col>
                <Col>
                    <label>Wählen Sie die Geräte aus, die angezeigt werden sollen</label>
                    <Select
                        options={mappedDevicesForSelect}
                        defaultValue={mappedDevicesForSelect[0]}
                        onChange={(selected) => setSelectedDevice(selected.value)}
                        styles={{
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                        }}
                    />
                </Col>
            </Row>
            <Row className="py-3">
                <Col>
                    <label className="mr-2">Auch bereits abgeschlossene Events anzeigen?</label>
                    <input
                        type="checkbox"
                        onChange={(e) => setShowSubmittedEvents(e.target.checked)}
                    />
                </Col>
                <Col>
                    <label className="mr-2">Auch Urlaube anzeigen?</label>
                    <input
                        type="checkbox"
                        defaultChecked={true}
                        onChange={(e) => setShowVacations(e.target.checked)}
                    />
                </Col>
                <Col className="d-flex align-items-center justify-content-end">
                    <span>Für {moment(range.start).format("DD.MM.YYYY")} - {moment(range.end).format("DD.MM.YYYY")} hat Aschur {aschurDays} Arbeitstage.</span>
                </Col>
            </Row>
            {(calendarEventState.isLoading || deviceState.isLoading || !types) && (
                <div style={{ position: "absolute" }}>
                    <Spinner animation="border" size={"sm"} variant={"primary"} />
                </div>
            )}
            <DnDCalendar
                onSelectEvent={onSelectEvent}
                onRangeChange={setNewRange}
                eventPropGetter={eventStyleGetter}
                defaultView={"week"}
                views={["month", "week", "day", "agenda"]}
                events={handleFilters(events).map((event: any) => mapEventForDisplay(event))}
                components={components}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventResize}
                draggableAccessor={draggableAccessor}
                resizableAccessor={resizableAccessor}
                selectable
                resizable
            />
        </>
    );
};

export default EventCalendar;
